/*
  useNotifications() — the signed-in user's inbox (backend: routes/notifications.py).

    const { unreadCount, items, loading, error, hasMore, loadList, loadMore, markRead, markAllRead, refreshCount } =
      useNotifications();

  - The unread count is fetched on mount, every `pollMs` (60 s) while the tab is visible, when the tab becomes
    visible again and on the window event `app:notifications-changed` (dispatch it after an action that is
    likely to notify the current user). No token → nothing is fetched.
  - `loadList()` loads the newest 20 (call it when the list opens); `loadMore()` pages back in time.
  - `markRead(ids)` / `markAllRead()` update the list at once and roll back if the server refuses.
*/
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

export const NOTIFICATIONS_CHANGED_EVENT = 'app:notifications-changed';
const PAGE_SIZE = 20;

const hasToken = () => {
  try {
    return Boolean(window.localStorage.getItem('token'));
  } catch {
    return false;
  }
};

export function useNotifications({ pollMs = 60_000, enabled = true } = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const mounted = useRef(true);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refreshCount = useCallback(async () => {
    if (!enabled || !hasToken()) return;
    try {
      const res = await api.get('/notifications/unread-count');
      if (mounted.current) setUnreadCount(Number(res.data?.unread_count) || 0);
    } catch {
      // the bell keeps its last count; the list shows errors when opened
    }
  }, [enabled]);

  const loadList = useCallback(async () => {
    if (!enabled || !hasToken()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/notifications', { params: { limit: PAGE_SIZE } });
      if (!mounted.current) return;
      setItems(res.data?.items || []);
      setUnreadCount(Number(res.data?.unread_count) || 0);
      setNextBefore(res.data?.next_before || null);
      setLoaded(true);
    } catch (err) {
      if (mounted.current) setError(err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [enabled]);

  const loadMore = useCallback(async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api.get('/notifications', { params: { limit: PAGE_SIZE, before: nextBefore } });
      if (!mounted.current) return;
      setItems((current) => {
        const seen = new Set(current.map((item) => item.id));
        return [...current, ...(res.data?.items || []).filter((item) => !seen.has(item.id))];
      });
      setNextBefore(res.data?.next_before || null);
    } catch (err) {
      if (mounted.current) setError(err);
    } finally {
      if (mounted.current) setLoadingMore(false);
    }
  }, [nextBefore, loadingMore]);

  const markRead = useCallback(async (ids) => {
    const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    const unreadIds = new Set(itemsRef.current.filter((item) => list.includes(item.id) && !item.read_at).map((item) => item.id));
    if (!list.length) return;
    const before = { items: itemsRef.current, unreadCount };
    const now = new Date().toISOString();
    setItems((current) => current.map((item) => (unreadIds.has(item.id) ? { ...item, read_at: now } : item)));
    setUnreadCount((count) => Math.max(0, count - unreadIds.size));
    try {
      const res = await api.post('/notifications/mark-read', { ids: list });
      if (mounted.current) setUnreadCount(Number(res.data?.unread_count) || 0);
    } catch (err) {
      if (mounted.current) {
        setItems(before.items);
        setUnreadCount(before.unreadCount);
      }
      throw err;
    }
  }, [unreadCount]);

  const markAllRead = useCallback(async () => {
    const before = { items: itemsRef.current, unreadCount };
    const now = new Date().toISOString();
    setItems((current) => current.map((item) => (item.read_at ? item : { ...item, read_at: now })));
    setUnreadCount(0);
    try {
      await api.post('/notifications/mark-all-read');
    } catch (err) {
      if (mounted.current) {
        setItems(before.items);
        setUnreadCount(before.unreadCount);
      }
      throw err;
    }
  }, [unreadCount]);

  useEffect(() => {
    if (!enabled) return undefined;
    refreshCount();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshCount();
    };
    const onChanged = () => {
      refreshCount();
    };
    const timer = pollMs > 0
      ? window.setInterval(() => {
        if (document.visibilityState !== 'hidden') refreshCount();
      }, pollMs)
      : null;
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    return () => {
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, onChanged);
    };
  }, [enabled, pollMs, refreshCount]);

  return {
    unreadCount,
    items,
    loading,
    loadingMore,
    loaded,
    error,
    hasMore: Boolean(nextBefore),
    loadList,
    loadMore,
    markRead,
    markAllRead,
    refreshCount,
  };
}

export default useNotifications;
